import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, CheckCircle2, FolderKanban, Sparkles, Wand2 } from 'lucide-react';
import { apiAiTaskerCreateProject } from '../api/client';

function priorityLabel(priority, t) {
  const key = priority === 'High' ? 'priorityHigh' : priority === 'Low' ? 'priorityLow' : 'priorityMedium';
  return t(key);
}

const MAX_RETRIES = 4;
const RETRY_DELAY_MS = 2000;

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function AiTaskerPage({ t, onToast }) {
  const navigate = useNavigate();
  const [description, setDescription] = useState('');
  const [taskCount, setTaskCount] = useState(8);
  const [loading, setLoading] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const submit = async (event) => {
    event.preventDefault();
    const trimmed = description.trim();
    if (trimmed.length < 20) {
      setError(t('aiTaskerDescriptionTooShort'));
      return;
    }

    setLoading(true);
    setError('');
    setResult(null);
    setRetryCount(0);

    let lastError = null;
    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      if (attempt > 0) {
        setRetryCount(attempt);
        await sleep(RETRY_DELAY_MS);
      }
      try {
        const created = await apiAiTaskerCreateProject({
          description: trimmed,
          taskCount: Number(taskCount) || 8,
        });
        setResult(created);
        setRetryCount(0);
        onToast?.(t('aiTaskerCreatedToast'), 'success');
        setLoading(false);
        return;
      } catch (err) {
        lastError = err;
      }
    }

    // All attempts exhausted
    const message = lastError instanceof Error ? lastError.message : t('aiTaskerFailed');
    setError(message);
    setRetryCount(0);
    onToast?.(message, 'error');
    setLoading(false);
  };

  const tasks = Array.isArray(result?.tasks ?? result?.Tasks) ? result.tasks ?? result.Tasks : [];
  const assignees = Array.isArray(result?.availableAssignees ?? result?.AvailableAssignees)
    ? result.availableAssignees ?? result.AvailableAssignees
    : [];
  const projectName = result?.projectName ?? result?.ProjectName;
  const projectNumber = result?.projectNumber ?? result?.ProjectNumber;
  const exampleDescription = t('aiTaskerExampleDescription');

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <section className="overflow-hidden rounded-3xl border border-slate-200 bg-gradient-to-br from-[#1A1A1A] via-brand-600 to-green-500 p-6 text-white shadow-xl shadow-brand/20 dark:border-slate-800">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
          <div className="max-w-3xl">
            <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-sm font-semibold">
              <Sparkles size={16} />
              {t('navAITasker')}
            </div>
            <h1 className="text-3xl font-bold md:text-4xl">{t('aiTaskerHeroTitle')}</h1>
            <p className="mt-3 text-sm leading-6 text-brand-50 md:text-base">
              {t('aiTaskerHeroBody')}
            </p>
          </div>
        </div>
      </section>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
        <form
          onSubmit={submit}
          className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900"
        >
          <div className="flex items-center gap-3">
            <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-brand-100 text-brand-700 dark:bg-brand-900/40 dark:text-brand-200">
              <Wand2 size={22} />
            </span>
            <div>
              <h2 className="text-xl font-bold text-slate-900 dark:text-white">{t('aiTaskerProjectDescriptionTitle')}</h2>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                {t('aiTaskerProjectDescriptionHelp')}
              </p>
            </div>
          </div>

          <textarea
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            rows={10}
            placeholder={exampleDescription}
            className="mt-5 w-full rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm leading-6 text-slate-800 outline-none transition focus:border-brand-400 focus:bg-white focus:ring-4 focus:ring-brand-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-brand-500 dark:focus:ring-brand-900/40"
          />

          <div className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <label className="text-sm font-semibold text-slate-700 dark:text-slate-200">
              {t('aiTaskerTaskCount')}
              <input
                type="number"
                min="3"
                max="20"
                value={taskCount}
                onChange={(event) => setTaskCount(event.target.value)}
                className="mt-2 block w-32 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-brand-400 focus:ring-4 focus:ring-brand-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
              />
            </label>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setDescription(exampleDescription)}
                className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
              >
                {t('useExample')}
              </button>
              <button
                type="submit"
                disabled={loading}
                className="inline-flex items-center gap-2 rounded-xl bg-brand-600 px-5 py-2 text-sm font-semibold text-white shadow-lg shadow-brand/20 transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <Sparkles size={17} className={loading ? 'animate-spin' : ''} />
                {loading
                  ? retryCount > 0
                    ? `${t('creating')} (${t('aiTaskerRetrying')} ${retryCount}/${MAX_RETRIES}…)`
                    : t('creating')
                  : t('createWithAI')}
              </button>
            </div>
          </div>

          {error && (
            <div className="mt-5 flex items-start gap-2 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-200">
              <AlertCircle size={18} className="mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </form>

        <aside className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <h2 className="text-lg font-bold text-slate-900 dark:text-white">{t('howItWorks')}</h2>
          <div className="mt-4 space-y-4 text-sm text-slate-600 dark:text-slate-300">
            <p>{t('aiTaskerStepOne')}</p>
            <p>{t('aiTaskerStepTwo')}</p>
            <p>{t('aiTaskerStepThree')}</p>
          </div>
          <div className="mt-5 rounded-2xl bg-slate-50 p-4 text-xs leading-5 text-slate-500 dark:bg-slate-950 dark:text-slate-400">
            {t('aiTaskerInviteHint')}
          </div>
        </aside>
      </div>

      {result && (
        <section className="rounded-3xl border border-emerald-200 bg-white p-6 shadow-sm dark:border-emerald-900/60 dark:bg-slate-900">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex items-start gap-3">
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200">
                <CheckCircle2 size={22} />
              </span>
              <div>
                <p className="text-sm font-semibold text-emerald-700 dark:text-emerald-300">{t('projectCreated')}</p>
                <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
                  {projectName} {projectNumber ? <span className="text-base text-slate-400">({projectNumber})</span> : null}
                </h2>
              </div>
            </div>
            <button
              type="button"
              onClick={() => navigate('/projects')}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-800 dark:bg-white dark:text-slate-900"
            >
              <FolderKanban size={17} />
              {t('openProjects')}
            </button>
          </div>

          <div className="mt-6 grid gap-3">
            {tasks.map((task) => {
              const id = task.id ?? task.Id;
              const title = task.title ?? task.Title;
              const priority = task.priority ?? task.Priority;
              const assignee = task.assigneeName ?? task.AssigneeName;
              const descriptionText = task.description ?? task.Description;
              return (
                <article
                  key={id ?? title}
                  className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950"
                >
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                    <h3 className="font-semibold text-slate-900 dark:text-white">{title}</h3>
                    <span className="rounded-full bg-brand-100 px-3 py-1 text-xs font-semibold text-brand-700 dark:bg-brand-900/40 dark:text-brand-200">
                      {priorityLabel(priority, t)}
                    </span>
                  </div>
                  {descriptionText && (
                    <p className="mt-2 line-clamp-3 text-sm leading-6 text-slate-600 dark:text-slate-300">
                      {descriptionText}
                    </p>
                  )}
                  <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-slate-400">
                    {t('assignedTo')}: {assignee || t('unassigned')}
                  </p>
                </article>
              );
            })}
          </div>

          {assignees.length > 0 && (
            <p className="mt-4 text-sm text-slate-500 dark:text-slate-400">
              {t('aiTaskerAvailableUsersPrefix')} {assignees.length}{' '}
              {t(assignees.length === 1 ? 'aiTaskerAvailableUsersSingular' : 'aiTaskerAvailableUsersPlural')}
            </p>
          )}
        </section>
      )}
    </div>
  );
}

export default AiTaskerPage;
